import { FirebaseError } from '@firebase/util'
import * as Sentry from '@sentry/nextjs'
import {
  AuthProvider,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  PhoneAuthProvider,
  User,
  UserCredential,
  signOut as authSignOut,
  fetchSignInMethodsForEmail,
  getAuth,
  linkWithCredential,
  sendPasswordResetEmail,
  signInAnonymously,
  signInWithCredential,
  signInWithEmailAndPassword,
  signInWithPopup,
} from 'firebase/auth'
import app from '~/src/lib/firebase/firebaseApp'
import logger from '~/src/lib/logger'

export { GoogleAuthProvider, EmailAuthProvider, sendPasswordResetEmail }

export const isValidEmail = (email?: string): boolean => {
  if (!email) return false
  const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return pattern.test(email)
}

// Initialize Firebase Authentication and get a reference to the service
const auth = getAuth(app)

/*
AnonymousProvider used to match the AuthProvider interface
from Firebase for the anonymous login option
*/
export class AnonymousProvider implements AuthProvider {
  static readonly PROVIDER_ID = 'anonymous'
  providerId = AnonymousProvider.PROVIDER_ID
}

// Google Provider Setup
const providerGoogle = new GoogleAuthProvider()
providerGoogle.setCustomParameters({
  prompt: 'select_account',
})
// https://developers.google.com/identity/protocols/oauth2/scopes
providerGoogle.addScope('https://www.googleapis.com/auth/userinfo.email')
providerGoogle.addScope('https://www.googleapis.com/auth/userinfo.profile')

// Apple Provider Setup
const providerApple = new OAuthProvider('apple.com')
providerApple.addScope('email')
providerApple.addScope('name')

/* AppleProvider used currently to detect when the user
has an existing Apple login linked to their Firebase account */
export class AppleProvider implements AuthProvider {
  static readonly PROVIDER_ID = 'apple.com'
  providerId = AppleProvider.PROVIDER_ID
}

const phoneProvider =
  typeof window !== 'undefined'
    ? new PhoneAuthProvider(auth)
    : ({} as AuthProvider)

export type SupportedProviderId =
  | typeof GoogleAuthProvider.PROVIDER_ID
  | typeof PhoneAuthProvider.PROVIDER_ID
  | typeof AppleProvider.PROVIDER_ID
  | typeof EmailAuthProvider.PROVIDER_ID

export const SUPPORTED_PROVIDER_MAPPING: {
  [key in SupportedProviderId]: AuthProvider
} = {
  [GoogleAuthProvider.PROVIDER_ID]: providerGoogle,
  [PhoneAuthProvider.PROVIDER_ID]: phoneProvider,
  [AppleProvider.PROVIDER_ID]: providerApple,
  [EmailAuthProvider.PROVIDER_ID]: new EmailAuthProvider(),
}

export interface SignInReturnValue {
  credential: UserCredential | undefined
}

export const signinWithEmail = async ({
  email,
  password,
}: {
  email: string
  password: string
}): Promise<UserCredential | undefined> => {
  if (!email || !password) {
    throw new FirebaseError(
      'auth/invalid-email',
      'Email and password are required'
    )
  }

  try {
    return await signInWithEmailAndPassword(auth, email, password)
  } catch (error: any) {
    logger.debug('[signinWithEmail] error', { error })
    throw error
  }
}

export const signInWithProvider = async ({
  providerId,
}: {
  providerId: SupportedProviderId
}): Promise<UserCredential | undefined> => {
  try {
    return await signInWithPopup(auth, SUPPORTED_PROVIDER_MAPPING[providerId])
  } catch (error: any) {
    switch (error?.code) {
      case 'auth/cancelled-popup-request':
      case 'auth/popup-closed-by-user':
        return undefined
      case 'auth/credential-already-in-use': {
        const pendingCred = OAuthProvider.credentialFromError(error)
        const currentUser = auth.currentUser

        if (pendingCred && currentUser) {
          // sign in to existing account
          return await signInWithCredential(auth, pendingCred)
        }

        throw error
      }
      case 'auth/email-already-in-use':
      case 'auth/account-exists-with-different-credential': {
        const pendingCred = OAuthProvider.credentialFromError(error)
        const email = error.customData.email as string
        const signInMethods = await fetchSignInMethodsForEmail(auth, email)
        const currentUser = auth.currentUser

        // try to link the social account to the existing account
        if (pendingCred && currentUser) {
          await linkWithCredential(currentUser, pendingCred)
          return undefined
        } else {
          const message = `Account with email (${email}) already exists using ${signInMethods.join(
            ', '
          )}.<br />Please sign in with the existing account.`

          Sentry.captureException(error, {
            user: {
              email,
            },
            extra: {
              signInMethods,
            },
          })

          throw new FirebaseError(error.code, message, {
            ...error.customData,
            signInMethods,
            pendingCred,
          })
        }
      }
      default:
        throw error
    }
  }
}

export const signInAnonymous = async (): Promise<User | undefined> => {
  try {
    const currentUser = auth.currentUser

    if (currentUser) {
      return currentUser
    }

    const userCredential = await signInAnonymously(auth)
    return userCredential.user
  } catch (error: any) {
    logger.debug('[signInAnonymous] error', { error })
    throw error
  }
}

export const signOut = async () => {
  await authSignOut(auth)
}

export default auth
